import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  ListItemSecondaryAction,
  Tab,
  Tabs,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Close,
  ContentCopy,
  Email,
  Link as LinkIcon,
  PersonAdd,
  CheckCircle,
  Cancel
} from '@mui/icons-material';
import { projectService } from '../services/api';
import { TEAM_ROLES, ACCESS_LEVELS } from '../constants/projectOptions';

const ShareProjectDialog = ({ open, onClose, project, onUpdate, initialTab = 0 }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [functionalRole, setFunctionalRole] = useState('');
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      if (project?.id) {
        loadShareLink();
        if (project.user_role === 'owner') {
          loadAccessRequests();
        }
      }
    }
  }, [open, project, initialTab]);

  const loadShareLink = async () => {
    try {
      const response = await projectService.getShareLink(project.id);
      setShareLink(response.data.shareLink);
    } catch (error) {
      console.error('Failed to load share link');
    }
  };

  const loadAccessRequests = async () => {
    try {
      const response = await projectService.getAccessRequests(project.id);
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load requests');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setSnackbar({ open: true, message: 'Lien copié !', severity: 'success' });
  };

  const handleInvite = async () => {
    if (!email) return;
    setLoading(true);
    try {
      await projectService.inviteUser(project.id, email, role, functionalRole);
      setSnackbar({ open: true, message: 'Invitation envoyée', severity: 'success' });
      setEmail('');
    } catch (error) {
      console.error(error);
      const errorMessage = error.response?.data?.details || error.response?.data?.warning || error.response?.data?.error || "Erreur lors de l'envoi";
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRequestResponse = async (requestId, status) => {
    try {
      await projectService.respondToRequest(project.id, requestId, status);
      setRequests(requests.filter(r => r.id !== requestId));
      setSnackbar({ open: true, message: status === 'approved' ? 'Accès accordé' : 'Demande refusée', severity: 'success' });
      if (status === 'approved' && onUpdate) onUpdate();
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur lors du traitement", severity: 'error' });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="span">Partager le projet</Typography>
          <IconButton onClick={onClose}><Close /></IconButton>
        </DialogTitle>
        
        <DialogContent dividers>
          <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 2 }}>
            <Tab label="Inviter" />
            {project?.user_role === 'owner' && <Tab label={`Demandes (${requests.length})`} />}
          </Tabs>

          {activeTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Share Link Section */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon fontSize="small" /> Lien public
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={shareLink}
                    InputProps={{ readOnly: true }}
                    variant="outlined"
                  />
                  <Button variant="outlined" onClick={handleCopyLink} startIcon={<ContentCopy />}>
                    Copier
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Toute personne avec ce lien pourra demander l'accès au projet.
                </Typography>
              </Box>

              {/* Email Invite Section */}
              <Box>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Email fontSize="small" /> Inviter par email
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="exemple@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Rôle d'accès</InputLabel>
                      <Select
                        value={role}
                        label="Rôle d'accès"
                        onChange={(e) => setRole(e.target.value)}
                      >
                        {ACCESS_LEVELS.map((level) => (
                          <MenuItem key={level.value} value={level.value}>
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Rôle fonctionnel</InputLabel>
                      <Select
                        value={functionalRole}
                        label="Rôle fonctionnel"
                        onChange={(e) => setFunctionalRole(e.target.value)}
                      >
                        {TEAM_ROLES.map((role) => (
                          <MenuItem key={role} value={role}>
                            {role}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <Button 
                    variant="contained" 
                    onClick={handleInvite} 
                    disabled={!email || loading}
                    startIcon={<PersonAdd />}
                    fullWidth
                  >
                    Envoyer l'invitation
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <List>
              {requests.length === 0 ? (
                <Typography color="text.secondary" align="center" py={3}>
                  Aucune demande en attente
                </Typography>
              ) : (
                requests.map((req) => (
                  <ListItem key={req.id} divider>
                    <ListItemAvatar>
                      <Avatar src={req.avatar_url}>{req.name?.[0]}</Avatar>
                    </ListItemAvatar>
                    <ListItemText 
                      primary={req.name} 
                      secondary={req.email} 
                    />
                    <ListItemSecondaryAction>
                      <IconButton edge="end" color="success" onClick={() => handleRequestResponse(req.id, 'approved')}>
                        <CheckCircle />
                      </IconButton>
                      <IconButton edge="end" color="error" onClick={() => handleRequestResponse(req.id, 'rejected')}>
                        <Cancel />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ShareProjectDialog;